// src/api/series.ts

export type PgInterval =
  | '1 second'
  | '5 seconds'
  | '15 seconds'
  | '30 seconds'
  | '1 minute'
  | '5 minutes'
  | '15 minutes'
  | '1 hour';

export interface VWAPSeriesParams {
  symbol: string;
  startISO: string;
  endISO: string;
  bucket: PgInterval; // <-- use the widened type
}

export interface VWAPDataPoint {
  ts: string;
  symbol: string;
  vwap: number; // non-null AFTER mapping below
}

export type VWAPRow = { ts: string; symbol: string; vwap: number | null };

export interface TradeSeriesRow {
  ts: string;
  price: number | null;
  volume: number;
  vwap?: number | null;
}

const API_BASE = process.env.REACT_APP_INGEST_API_BASE || 'http://localhost:3001';

// --- Trades series ---
export async function fetchTradeSeries(
  symbol: string,
  startISO: string,
  endISO: string,
  bucket: '1 second' | '1 minute' | '5 minutes' | '15 minutes' = '1 minute'
): Promise<TradeSeriesRow[]> {
  const u = new URL(`${API_BASE}/api/series/${encodeURIComponent(symbol)}/trades`);
  u.searchParams.set('start', startISO);
  u.searchParams.set('end', endISO);
  u.searchParams.set('bucket', bucket);
  const r = await fetch(u.toString());
  if (!r.ok) throw new Error('trade series failed');
  return r.json();
}

// --- VWAP helpers ---


// If you want a strictly non-null VWAP series for charts:
export async function fetchVWAPSeries(params: VWAPSeriesParams): Promise<VWAPDataPoint[]> {
  const { symbol, startISO, endISO, bucket } = params;
  const u = new URL(`${API_BASE}/api/series/${encodeURIComponent(symbol)}/vwap`);
  u.searchParams.set('start', startISO);
  u.searchParams.set('end', endISO);
  u.searchParams.set('bucket', bucket);
  const r = await fetch(u.toString());
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  const rows: VWAPRow[] = await r.json();

  // Enforce non-null vwap for the chart consumer
  return rows
    .filter(row => row.vwap != null)
    .map(row => ({ ts: row.ts, symbol: row.symbol, vwap: Number(row.vwap) }));
}



// Simple VWAP fetch for basic charts (no time range required)
export async function fetchSimpleVWAP(
  symbol: string,
  limit: number = 100
): Promise<VWAPDataPoint[]> {
  const u = new URL(`${API_BASE}/api/series/${encodeURIComponent(symbol)}/vwap`);
  u.searchParams.set('limit', limit.toString());
  const r = await fetch(u.toString());
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  
  // Check if response is JSON before parsing
  const contentType = r.headers.get('content-type');
  if (!contentType || !contentType.includes('application/json')) {
    const text = await r.text();
    if (text.trim().startsWith('<!DOCTYPE')) {
      throw new Error('API returned HTML instead of JSON. Server may not be running or endpoint does not exist.');
    }
    throw new Error(`Unexpected content type: ${contentType}`);
  }
  
  const rows: VWAPDataPoint[] = await r.json();
  return rows;
}

// Simple Microprice fetch for basic charts (no time range required)
export async function fetchSimpleMicroprice(
  symbol: string,
  limit: number = 100
): Promise<MicropriceDataPoint[]> {
  const u = new URL(`${API_BASE}/api/series/${encodeURIComponent(symbol)}/microprice`);
  u.searchParams.set('limit', limit.toString());
  const r = await fetch(u.toString());
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  
  // Check if response is JSON before parsing
  const contentType = r.headers.get('content-type');
  if (!contentType || !contentType.includes('application/json')) {
    const text = await r.text();
    if (text.trim().startsWith('<!DOCTYPE')) {
      throw new Error('API returned HTML instead of JSON. Server may not be running or endpoint does not exist.');
    }
    throw new Error(`Unexpected content type: ${contentType}`);
  }
  
  const rows: any[] = await r.json();
  return rows.filter(row => row.microprice != null && row.microprice !== '').map(row => ({ 
    ts: row.ts, 
    symbol: row.symbol, 
    microprice: Number(row.microprice),
    imbalance: Number(row.imbalance || 0),
    spread: Number(row.spread || 0)
  }));
}

// Removed: currently use WebSocket data
// Types kept for future reference:
// export interface EODDataPoint { ... }

// Simple TWAP fetch for basic charts (no time range required) - Multiple timeframes
export async function fetchSimpleTWAP(
  symbol: string,
  limit: number = 100
): Promise<TWAPDataPoint[]> {
  const u = new URL(`${API_BASE}/api/series/${encodeURIComponent(symbol)}/twap`);
  u.searchParams.set('limit', limit.toString());
  const r = await fetch(u.toString());
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  
  // Check if response is JSON before parsing
  const contentType = r.headers.get('content-type');
  if (!contentType || !contentType.includes('application/json')) {
    const text = await r.text();
    if (text.trim().startsWith('<!DOCTYPE')) {
      throw new Error('API returned HTML instead of JSON. Server may not be running or endpoint does not exist.');
    }
    throw new Error(`Unexpected content type: ${contentType}`);
  }
  
  const rows: any[] = await r.json();
  return rows.filter(row => row.twap_1m != null && row.twap_1m !== '').map(row => ({ 
    ts: row.ts, 
    symbol: row.symbol, 
    twap_1m: Number(row.twap_1m),
    twap_5m: Number(row.twap_5m),
    twap_15m: Number(row.twap_15m),
    twap_30m: Number(row.twap_30m),
    twap_1h: Number(row.twap_1h),
    twap_4h: Number(row.twap_4h),
    last_price: Number(row.last_price)
  }));
}

// Simple combined fetch for basic charts (no time range required)
export async function fetchSimpleCombined(
  symbol: string,
  limit: number = 100
): Promise<CombinedDataPoint[]> {
  const u = new URL(`${API_BASE}/api/series/${encodeURIComponent(symbol)}/combined`);
  u.searchParams.set('limit', limit.toString());
  const r = await fetch(u.toString());
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  const rows: CombinedDataPoint[] = await r.json();
  return rows.map(row => ({ 
    ts: row.ts, 
    symbol: row.symbol, 
    vwap: row.vwap ? Number(row.vwap) : undefined,
    twap_1m: row.twap_1m ? Number(row.twap_1m) : undefined,
    twap_5m: row.twap_5m ? Number(row.twap_5m) : undefined,
    twap_15m: row.twap_15m ? Number(row.twap_15m) : undefined,
    twap_30m: row.twap_30m ? Number(row.twap_30m) : undefined,
    twap_1h: row.twap_1h ? Number(row.twap_1h) : undefined,
    twap_4h: row.twap_4h ? Number(row.twap_4h) : undefined,
    last_price: row.last_price ? Number(row.last_price) : undefined,
    microprice: row.microprice ? Number(row.microprice) : undefined,
    spread: row.spread ? Number(row.spread) : undefined,
    imbalance: row.imbalance ? Number(row.imbalance) : undefined
  }));
}

// Types for functions that are still used (fetchSimpleTWAP, fetchSimpleMicroprice)
export interface TWAPDataPoint {
  ts: string;
  symbol: string;
  twap_1m: number;
  twap_5m: number;
  twap_15m: number;
  twap_30m: number;
  twap_1h: number;
  twap_4h: number;
  last_price: number;
}

export interface MicropriceDataPoint {
  ts: string;
  symbol: string;
  microprice: number;
  imbalance: number;
  spread?: number;
}

export interface CombinedDataPoint {
  ts: string;
  symbol: string;
  vwap?: number;
  twap_1m?: number;
  twap_5m?: number;
  twap_15m?: number;
  twap_30m?: number;
  twap_1h?: number;
  twap_4h?: number;
  last_price?: number;
  microprice?: number;
  spread?: number;
  imbalance?: number;
}
